import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RocketLaunchIcon,
  HeartIcon,
  LightBulbIcon,
  UserGroupIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import BrandLogo from '../components/shared/BrandLogo';
import { BRAND_HERO_GRADIENT } from '../components/shared/SectionTitle';

const AboutPage = () => {
  const values = [
    {
      icon: HeartIcon,
      title: 'Creator-First',
      description: 'We build tools for creators, by understanding creators. Every feature is designed to solve real creator problems.',
    },
    {
      icon: LightBulbIcon,
      title: 'Innovation',
      description: 'We leverage cutting-edge AI and machine learning to provide insights that help creators stay ahead.',
    },
    {
      icon: UserGroupIcon,
      title: 'Community',
      description: 'We believe in the power of community. Creators helping creators build better businesses together.',
    },
    {
      icon: TrophyIcon,
      title: 'Excellence',
      description: 'We\'re committed to delivering a best-in-class platform that creators can rely on every day.',
    },
  ];

  const team = [
    /*{
      name: 'Sarah Johnson',
      role: 'CEO & Co-Founder',
      bio: 'Former creator with 10M+ followers. Built OmniConnX to solve the problems she faced managing multiple platforms.',
      image: '👩‍💼',
    },
    {
      name: 'Rakesh Dash',
      role: 'CTO & Co-Founder',
      bio: 'Ex-Google engineer with expertise in ML and distributed systems. Passionate about building tools for creators.',
      image: '👨‍💻',
    },
    {
      name: 'Emily Rodriguez',
      role: 'Head of Product',
      bio: 'Product leader from Meta. Obsessed with user experience and building intuitive creator tools.',
      image: '👩‍🎨',
    },
    {
      name: 'David Kim',
      role: 'Head of Engineering',
      bio: 'Built scalable systems at Netflix. Ensures OmniConnX handles millions of data points seamlessly.',
      image: '👨‍🔬',
    },*/
  ];

  const stats = [
    { value: '500+', label: 'Active Creators', sub: 'and growing every week' },
    { value: '1M+', label: 'Metrics Tracked', sub: 'across all platforms' },
    { value: '$4M+', label: 'Revenue Managed', sub: 'by creators on OmniConnX' },
    { value: '99.5%', label: 'Uptime', sub: 'since private beta' },
  ];

  const milestones = [
    {
      year: 'Mar 2026',
      event: 'OmniConnX Founded',
      description: 'A creator frustrated by fragmented analytics tools decided to build the platform they always wished existed.',
      current: false,
    },
    {
      year: 'Jun 2026',
      event: 'Private Beta',
      description: 'Launched a closed beta with hand-picked creators. Their feedback is shaping every core feature we ship.',
      current: true,
    },
    {
      year: 'Sep 2026',
      event: 'Public Launch 🚀',
      description: 'Opening the platform to everyone — YouTube, TikTok, Instagram, Facebook, Twitch and X integrations on day one.',
      current: false,
    },
    /* ── Previous timeline (kept for reference) ─────────────────────────
    {
      year: 'Jan 2025',
      event: 'OmniConnX Founded',
      description: 'A creator frustrated by fragmented analytics tools decided to build the platform they always wished existed.',
      current: false,
    },
    {
      year: 'Apr 2025',
      event: 'Private Beta',
      description: 'Launched a closed beta with 50 hand-picked creators. Their feedback shaped every core feature we ship today.',
      current: false,
    },
    {
      year: 'Jul 2025',
      event: 'First 100 Creators',
      description: 'Hit 100 active users — 100% organic, zero paid marketing. Word of mouth from creators who saw real results.',
      current: false,
    },
    {
      year: 'Oct 2025',
      event: 'Public Launch & v1.0',
      description: 'Opened the platform to everyone. Launched YouTube, TikTok, Instagram and Twitter/X integrations on day one.',
      current: false,
    },
    {
      year: 'Dec 2025',
      event: 'AI Anomaly Detection',
      description: 'Shipped ML-powered health monitoring. Creators now get proactive alerts before problems become crises.',
      current: false,
    },
    {
      year: 'Now 🚀',
      event: 'Growing Fast',
      description: 'Over 500 creators trust OmniConnX to run their business. We\'re shipping new features every two weeks and just getting started.',
      current: true,
    },
    ──────────────────────────────────────────────────────────────────── */
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      {/* Header */}
      <nav className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <BrandLogo size="md" />
            </Link>

            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                Home
              </Link>
              <Link to="/login" className="btn btn-secondary">
                Login
              </Link>
              <Link to="/register" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        {/* Brand gradient — matches the OmniConnX logo (orange → pink → violet → blue) */}
        <div className="absolute inset-0" style={{ background: BRAND_HERO_GRADIENT }} />
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Empowering Creators Worldwide
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/80 max-w-3xl mx-auto mb-6"
          >
            We're on a mission to help every creator build a sustainable, data-driven business
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/95 dark:bg-dark-900/70 border border-white/40 dark:border-dark-700 rounded-full px-5 py-2 text-sm text-gray-600 dark:text-gray-300 shadow-sm"
          >
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Founded March 2026 · Private beta live
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold bg-gradient-to-r from-pink-500 to-violet-600 bg-clip-text text-transparent mb-1">
                  {stat.value}
                </div>
                <div className="text-gray-900 dark:text-white font-medium text-sm mb-1">{stat.label}</div>
                <div className="text-gray-500 dark:text-gray-400 text-xs">{stat.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <RocketLaunchIcon className="w-16 h-16 mx-auto text-violet-600 dark:text-violet-400 mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              We believe the creator economy is the future of work. OmniConnX was born in 2026 out of a simple
              frustration: creators were managing their entire business across a dozen disconnected tabs. We set
              out to build the one platform that unifies analytics, revenue, health monitoring, and brand deals —
              giving every creator the operating system their business deserves.
            </p>
            <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
              We opened our private beta in June 2026 and are gearing up for public launch in September 2026.
              Every feature we ship is driven by direct feedback from the creators already using OmniConnX
              every day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-100 dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-12 text-center">
            Our Values
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 text-center"
              >
                <value.icon className="w-12 h-12 mx-auto text-violet-600 dark:text-violet-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 text-center">
            Meet Our Team
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            We're a diverse team of creators, engineers, and designers passionate about empowering the creator economy
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 text-center"
              >
                <div className="text-6xl mb-4">{member.image}</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {member.name}
                </h3>
                <p className="text-violet-600 dark:text-violet-400 text-sm mb-3">
                  {member.role}
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gray-100 dark:bg-dark-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 text-center">
            Our Journey
          </h2>
          <p className="text-center text-gray-500 dark:text-gray-400 mb-12">
            From idea to platform — founded, built and launching in 2026.
          </p>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[7.5rem] top-0 bottom-0 w-px bg-gray-200 dark:bg-dark-700 hidden sm:block" />

            <div className="space-y-6">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="flex gap-6 items-start"
                >
                  {/* Year badge */}
                  <div className="flex-shrink-0 w-28 text-right pt-5">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                      milestone.current
                        ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-md shadow-violet-200 dark:shadow-violet-900'
                        : 'bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
                    }`}>
                      {milestone.year}
                    </span>
                  </div>

                  {/* Dot on the line */}
                  <div className="relative flex-shrink-0 hidden sm:flex items-center justify-center w-4 mt-5">
                    <div className={`w-3 h-3 rounded-full border-2 ${
                      milestone.current
                        ? 'bg-violet-600 border-violet-600 ring-4 ring-violet-100 dark:ring-violet-900/30'
                        : 'bg-white dark:bg-dark-900 border-violet-400 dark:border-violet-600'
                    }`} />
                  </div>

                  {/* Card */}
                  <div className={`flex-1 p-5 rounded-xl border transition-all ${
                    milestone.current
                      ? 'bg-violet-50 dark:bg-violet-900/10 border-violet-200 dark:border-violet-800 shadow-sm'
                      : 'bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-700'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-semibold ${
                        milestone.current
                          ? 'text-violet-700 dark:text-violet-400'
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {milestone.event}
                      </h3>
                      {milestone.current && (
                        <span className="text-xs bg-gradient-to-r from-pink-500 to-violet-600 text-white px-2 py-0.5 rounded-full font-medium">
                          Live
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Careers CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
            Join Our Team
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            We're always looking for talented people who share our passion for empowering creators
          </p>
          <a href="#" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md">
            View Open Positions
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20">
        {/* Brand gradient — matches the OmniConnX logo (orange → pink → violet → blue) */}
        <div className="absolute inset-0" style={{ background: BRAND_HERO_GRADIENT }} />
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start managing your creator business like a pro
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn bg-white text-violet-700 hover:bg-violet-50 shadow-md">
              Get Started Free
            </Link>
            <Link to="/contact" className="btn bg-violet-950/60 text-white hover:bg-violet-950/80 ring-1 ring-white/40">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-900 border-t border-gray-200 dark:border-dark-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-600 dark:text-gray-400">
          <p>&copy; 2026 OmniConnX. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
